import { z } from 'zod';
import { numberParameter, overrideZoom, overrideZoomParameter } from './shared.js';

// PromptingGuide p148: omitted GLS options retain native settings; a GAU
// diameter and GAU No are distinct commands, not interchangeable blank values.
const lineSpreadRow = z.union([
  z.strictObject({ zoom: overrideZoom, rays_across_diameter: z.number().int() }),
  z.strictObject({ zoom: overrideZoom, rays_across_diameter: z.number().int().nullable().optional(),
    gaussian_mode: z.literal('No'),
  }),
  z.strictObject({ zoom: overrideZoom, rays_across_diameter: z.number().int().nullable().optional(),
    gaussian_mode: z.literal('Diameter'), gaussian_diameter: z.number(),
  }),
]);
const lineSpreadSettings = z.strictObject({
  option_settings: z.array(lineSpreadRow).default([]).describe('Ordered GLS overrides. Omission retains native settings: about 316 rays weighted by wavelength (NRD 20 for one wavelength); the native Gaussian smoothing default is described differently by PromptingGuide p148 (1/200 of the spot diameter) and the GLS dialog reference (1/200 of image height or width). No passes the native GAU No choice; Diameter supplies its native 50%-intensity diameter. A missing cell does not reset an earlier row.'),
});
export default {
  label: 'Geometrical Line Spread', group: 'performance',
  description: 'Geometrical line spread and edge spread, with radial plots.',
  settingsSchema: lineSpreadSettings,
  parameters: [
    { key: 'option_settings', label: 'Options', kind: 'rows', columns: [overrideZoomParameter,
      { ...numberParameter('rays_across_diameter', 'Rays across diameter', { integer: true, blank: 'Unchanged' }), omit: { first: 'Default', later: 'Unchanged' } },
      { key: 'gaussian_mode', label: 'Gaussian smoothing', kind: 'select', omit: { first: 'Default', later: 'Unchanged' },
        options: [{ value: 'No', label: 'No' }, { value: 'Diameter', label: 'Diameter' }],
      },
      numberParameter('gaussian_diameter', 'Gaussian diameter', { when: { gaussian_mode: ['Diameter'] } }),
    ] },
  ],
  coverage: 'Focal (non-afocal) systems only. Plots are by sample number, with the scan interval and ray statistics listed below. Tangential values are listed but not plotted, because for a rotationally symmetric lens CODE V can leave them uncomputed as zeros. Ray statistics apply to the field named in the listing.',
} as const;
